import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import PaginationDashboard from "./PaginationDashboard";

const BarChart = ({
  data,
  width = 500,
  height = 300,
  colors = d3.schemeCategory10,
  legendTitle = "Categories",
  isShowPagination,
  totalPages,
  currentPage,
  loadPrevious,
  loadNext,
  handleOnchange,
  handlePaginationOnBlur,
  onPageClick,
}) => {
  const svgRef = useRef();

  useEffect(() => {
    if (!data || data.length === 0) return;

    const margin = { top: 100, right: 25, bottom: 70, left: 80 };

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    svg.selectAll("*").remove();

    // FIX 1: Coerce null/undefined name to empty string so it never renders as "null"
    const safeName = (d) => (d.name != null ? String(d.name) : "");

    const xScale = d3
      .scaleBand()
      .domain(data.map((d) => `${d.category} ~ ${safeName(d)}`))
      .range([margin.left, width - margin.right])
      .padding(0.2);

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d) => d.value)])
      .nice()
      .range([height - margin.bottom, margin.top]);

    const colorScale = d3.scaleOrdinal(colors);

    const words = legendTitle.trim().split(/\s+/);
    const xAxisLabel = words[0] + " ";
    const yAxisLabel = `Number of ${words[words.length - 1]}`;

    // Draw bars
    svg
      .selectAll("rect")
      .data(data)
      .join("rect")
      .attr("x", (d) => xScale(`${d.category} ~ ${safeName(d)}`))
      .attr("y", (d) => yScale(d.value))
      .attr("width", xScale.bandwidth())
      .attr("height", (d) => height - margin.bottom - yScale(d.value))
      .attr("fill", (d) => colorScale(d.category));

    svg
      .selectAll("rect")
      .each(function () {
        const rect = d3.select(this);
        const currentFill = rect.attr("fill") || "blue";
        rect.attr("data-original-fill", currentFill);
      })
      .on("mouseover", function () {
        d3.select(this).transition().duration(300).attr("fill", "blue");
      })
      .on("mouseout", function () {
        const rect = d3.select(this);
        const originalFill = rect.attr("data-original-fill");
        rect.transition().duration(300).attr("fill", originalFill);
      });

    // Add X Axis
    svg
      .append("g")
      .attr("transform", `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(xScale))
      .selectAll("text")
      .text("")
      .each(function (d) {
        // FIX 1 cont: split on " ~ " and treat empty/missing name gracefully
        const parts = d.split(" ~ ");
        const category = parts[0];
        const name = parts[1] ?? ""; // empty string if absent

        if (!category) return;

        const el = d3.select(this);

        el.append("tspan").attr("x", 0).attr("dy", "0em").text(category);

        // Only render the name tspan if there is actual content
        if (name.length > 0) {
          el.append("tspan").attr("x", 0).attr("dy", "1em").text(`(${name})`);
        }
      })
      .attr("transform", "rotate(-15)")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .style("text-anchor", "end");

    // Add Y Axis
    svg
      .append("g")
      .attr("transform", `translate(${margin.left},0)`)
      .call(d3.axisLeft(yScale));

    svg
      .selectAll("text.bar-label")
      .data(data)
      .enter()
      .append("text")
      .attr("class", "bar-label")
      .attr("x", (d) => {
        const key = `${d.category} ~ ${safeName(d)}`;
        const x = xScale(key);
        return x != null ? x + xScale.bandwidth() / 2 : 0;
      })
      .attr("y", (d) => yScale(d.value) - 5)
      .style("font-weight", "bold")
      .attr("text-anchor", "start")
      .attr("writing-mode", "sideways-lr")
      .attr("fill", "black")
      .style("font-size", "12px")
      .text((d) => d.value);

    // X-Axis Label
    svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("x", (width - margin.left - margin.right) / 2 + margin.left)
      .attr("y", height - 20)
      .text(xAxisLabel + "→")
      .style("fill", "#FF5733")
      .style("font-weight", "bold")
      .style("font-size", "14px");

    // Y-Axis Label
    svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("transform", "rotate(-90)")
      .attr("x", -((height - margin.top - margin.bottom) / 2 + margin.top))
      .attr("y", 20)
      .text("→" + yAxisLabel)
      .style("fill", "#FF5733")
      .style("font-weight", "bold")
      .style("font-size", "14px");
  }, [data, width, height, colors]);

  return (
    <div>
      <svg ref={svgRef}> </svg>

      {isShowPagination && (
        <div className="custom_Pagination">
          <PaginationDashboard
            loadPrevious={(e) => loadPrevious(e)}
            loadNext={(e) => loadNext(e)}
            currentPage={currentPage}
            totalPages={totalPages}
            handleOnchange={(e) => handleOnchange(e)}
            handlePaginationOnBlur={(e) => handlePaginationOnBlur(e)}
            onPageClick={(e) => onPageClick(e)}
          />
        </div>
      )}
    </div>
  );
};

export { BarChart };
